import { getRandomId } from '@craftjs/utils'
import { isEqual, omit } from 'lodash'

import { buildDocument } from './document'
import { LIMITS } from './protocol'

const COMPARED_KEYS = ['name', 'classes', 'text', 'hidden', 'children', 'linked', 'canvas', 'custom', 'metadata']

function diffNodeDocs(from, to) {
  const change = {}
  const props = {}
  const keys = new Set([...Object.keys(from.props || {}), ...Object.keys(to.props || {})])

  for (const key of keys) {
    if (!isEqual(from.props?.[key], to.props?.[key])) {
      props[key] = { from: from.props?.[key], to: to.props?.[key] }
    }
  }

  if (Object.keys(props).length) {
    change.props = props
  }

  for (const key of COMPARED_KEYS) {
    if (!isEqual(from[key], to[key])) {
      change[key] = { from: from[key], to: to[key] }
    }
  }

  return Object.keys(change).length ? change : null
}

const indexWithin = (parentDoc, id) => parentDoc ? parentDoc.children.indexOf(id) : -1

const reordered = (fromChildren = [], toChildren = []) => {
  const commonFrom = fromChildren.filter(id => toChildren.includes(id))
  const commonTo = toChildren.filter(id => fromChildren.includes(id))

  return commonTo
    .filter(id => commonFrom.indexOf(id) !== commonTo.indexOf(id))
    .map(id => ({ id, fromIndex: fromChildren.indexOf(id), toIndex: toChildren.indexOf(id) }))
}

function diffIndexes(from, to, nodeOf) {
  const added = []
  const removed = []
  const moved = []
  const changed = []
  const parentOf = (index, id) => nodeOf(index.get(id))

  for (const [id, hash] of to) {
    const previous = from.get(id)

    if (previous === undefined) {
      added.push(nodeOf(hash))
      continue
    }

    if (previous === hash) {
      continue
    }

    const before = nodeOf(previous)
    const after = nodeOf(hash)

    if (before.parent !== after.parent) {
      moved.push({
        id,
        from: { parent: before.parent, index: indexWithin(parentOf(from, before.parent), id) },
        to: { parent: after.parent, index: indexWithin(parentOf(to, after.parent), id) },
      })
    }

    const change = diffNodeDocs(before, after)
    if (change) {
      changed.push({ id, block: after.block, ...change })
    }
  }

  for (const change of changed) {
    if (!change.children) {
      continue
    }

    for (const { id, fromIndex, toIndex } of reordered(change.children.from, change.children.to)) {
      if (!moved.some(item => item.id === id)) {
        moved.push({ id, from: { parent: change.id, index: fromIndex }, to: { parent: change.id, index: toIndex } })
      }
    }
  }

  for (const id of from.keys()) {
    if (!to.has(id)) {
      removed.push(id)
    }
  }

  return { added, removed, moved, changed }
}

export function createRevisionLog({ store, limit = LIMITS.revisionLog }) {
  const epoch = getRandomId()
  const nodes = new Map()
  const revisions = []
  let rev = 0
  let unsubscribe = null

  const retain = (hash, doc) => {
    const entry = nodes.get(hash)
    if (entry) {
      entry.refs++
      return
    }
    nodes.set(hash, { doc: omit(doc, ['index']), refs: 1 })
  }

  const release = hash => {
    const entry = nodes.get(hash)
    if (!entry) {
      return
    }
    entry.refs--
    if (entry.refs <= 0) {
      nodes.delete(hash)
    }
  }

  const nodeOf = hash => nodes.get(hash)?.doc

  const record = document => {
    const last = revisions[revisions.length - 1]
    if (last && last.hash === document.hash) {
      return last
    }

    rev++
    const index = new Map()
    for (const doc of document.nodes) {
      index.set(doc.id, doc.hash)
      retain(doc.hash, doc)
    }

    const entry = { rev, hash: document.hash, index }
    revisions.push(entry)

    while (revisions.length > limit) {
      const evicted = revisions.shift()
      evicted.index.forEach(hash => release(hash))
    }

    return entry
  }

  const capture = () => record(buildDocument(store.query.getSerializedNodes()))

  const idOf = entry => `${epoch}:${entry.rev}`

  const current = () => {
    const last = revisions[revisions.length - 1] || capture()
    return { id: idOf(last), rev: last.rev, hash: last.hash, epoch }
  }

  const find = value => {
    const [entryEpoch, entryRev] = String(value || '').split(':')
    const number = Number(entryRev)

    if (entryEpoch !== epoch || !Number.isInteger(number)) {
      return null
    }

    return revisions.find(entry => entry.rev === number) || null
  }

  const diffBetween = (fromId, toId) => {
    const from = find(fromId)
    const to = toId ? find(toId) : revisions[revisions.length - 1]

    if (!from || !to) {
      return { unknownBase: true, current: current() }
    }

    return {
      from: idOf(from),
      to: idOf(to),
      hash: to.hash,
      changes: diffIndexes(from.index, to.index, nodeOf),
    }
  }

  const start = () => {
    capture()
    unsubscribe = store.subscribe(state => state.nodes, () => capture())
  }

  const stop = () => {
    unsubscribe?.()
    unsubscribe = null
  }

  return { epoch, start, stop, capture, current, find, diffBetween }
}
